import { ComponentProps, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { stringToNumberOrNull } from '@fora-monorepo/string-utils';
import {
  Flex,
  InputField,
  Table,
  TableFeedbackOld,
  Text,
  Tooltip,
  useNumericInputField,
} from '@foradevelopment/fora-ui';
import { AttributablePay, PayReportingTableRowViewModelOld } from '@fora-monorepo/employer-my-pages-common/types';

import { formatEmployeeId } from '../../../common/utils';
import { AttributablePayModal, EmploymentTableRowIconCellOld } from '../../../components';
import { setEmploymentPayDraft } from '../../../redux/payroll/payrollThunks';
import { setEmploymentPay, setPayInputError } from '../../../redux/payroll/payrollSlice';
import { payAmountInputFormatter } from '../../utils/payAmountInputFormatter';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { getInputValidator } from '../../utils';
import { useTypedTranslation } from '../../../i18n/i18n';

type PayrollReportingTableRowProps = {
  payReportingTableData: PayReportingTableRowViewModelOld;
  index: number;
  showIconColumn: boolean;
  validateOnRender?: boolean;
};

export const PayrollReportingTableRowOld = ({
  payReportingTableData,
  index,
  showIconColumn,
  validateOnRender,
}: PayrollReportingTableRowProps) => {
  const { t } = useTypedTranslation();
  const dispatch = useAppDispatch();

  const statePayData = useAppSelector(state =>
    state.payroll.inputPayData[payReportingTableData.policyNumber]
      ? state.payroll.inputPayData[payReportingTableData.policyNumber]
      : undefined
  );
  const { highPayWarningLimit, payMonthMargin } = useAppSelector(state => state.foraGlobal.settings);
  const { payMonth, isPeriodOpen } = useAppSelector(
    state => state.payroll.selectedPayMonthOverview ?? { payMonth: '', isPeriodOpen: null }
  );
  const [showTableFeedbackWarning, setShowTableFeedBackWarning] = useState(true);
  const [validateSelfOnRender, setValidateSelfOnRender] = useState(false);

  const setPay = (payAmount: number | null, attributablePay?: AttributablePay) => {
    dispatch(setEmploymentPay({ payAmount, attributablePay, policyNumber: statePayData?.policyNumber ?? '' }));
    dispatch(
      setEmploymentPayDraft({ payAmount, attributablePay, policyNumber: statePayData?.policyNumber ?? '', payMonth })
    );
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    payInput.props.onBlur();

    const newPayAmount = stringToNumberOrNull(e.currentTarget.value);
    if (statePayData?.payAmount !== newPayAmount || newPayAmount === 0) {
      setPay(newPayAmount);
    }
  };

  const { payInputValidator, hasAttributablePay } = useMemo(() => {
    const hasAttributablePay = Boolean(
      statePayData?.attributablePay &&
        statePayData.attributablePay.length > 0 &&
        statePayData.attributablePay.some(attriPay => attriPay.attributablePayAmount > 0)
    );
    const inputValidator = getInputValidator({
      attributablePay: hasAttributablePay ? statePayData?.attributablePay : undefined,
      fokIsExpiring: payReportingTableData.activeMarkup?.fokIsExpiring,
      isPeriodOpen,
      reportedPayAmount: payReportingTableData.payAmountReported,
      personDeceased: !!payReportingTableData.employee.deathDate,
      isAbovePensionIncomeAge: payReportingTableData.isAbovePensionIncomeAge,
    });

    const payInputValidator = (value: string) => {
      const result = inputValidator(value);
      if (statePayData?.policyNumber) {
        dispatch(setPayInputError({ policyNumber: statePayData.policyNumber, error: result.length !== 0 }));
      }
      return result;
    };

    return { hasAttributablePay, payInputValidator };
  }, [
    dispatch,
    isPeriodOpen,
    payReportingTableData.activeMarkup?.fokIsExpiring,
    payReportingTableData.employee.deathDate,
    payReportingTableData.isAbovePensionIncomeAge,
    payReportingTableData.payAmountReported,
    statePayData?.attributablePay,
    statePayData?.policyNumber,
  ]);

  const payInput = useNumericInputField(
    statePayData?.payAmount?.toString() ?? '',
    payInputValidator,
    payAmountInputFormatter,
    false
  );

  useLayoutEffect(() => {
    if (validateOnRender || statePayData?.hasError) {
      payInput.validateInputField();
    }
    // We only want to run this on first render and when validateOnRender turns on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateOnRender]);

  useLayoutEffect(() => {
    if (validateSelfOnRender) {
      payInput.validateInputField();
      setValidateSelfOnRender(false);
    }
    // We only want to run this when validateSelfOnRender turns on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateSelfOnRender]);

  const onSaveAttributablePay = (attributablePayInput: AttributablePay) => {
    setPay(statePayData?.payAmount ?? null, attributablePayInput);
  };

  const onKeyBoardEvent = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      const nextInputField = document.getElementById(`pay-input-field-${index + 1}`);
      nextInputField?.focus(); // Go to the next input field with Enter or Down key
    }
    if (e.key === 'ArrowUp') {
      const previousInputField = document.getElementById(`pay-input-field-${index - 1}`);
      previousInputField?.focus(); // Go to the previous input field with Up key
    }
  };

  const payrollError = payInput.props.errors[0] || undefined;
  const payrollWarning =
    highPayWarningLimit && Number(stringToNumberOrNull(payInput.props.value)) >= highPayWarningLimit
      ? t('errorMessages.validation.payroll.highPayWarning')
      : undefined;

  useEffect(() => {
    if (payrollWarning) {
      setShowTableFeedBackWarning(true);
    }
  }, [payrollWarning]);

  const isAttributablePayError =
    payrollError === t('errorMessages.validation.payroll.attributablePayIsLowerThanPayroll');
  const isDecreasedPayError =
    payrollError === t('errorMessages.validation.payroll.pensionAgedIndividual') ||
    payrollError === t('errorMessages.validation.payroll.deceasedIndividual');

  const resetToReportedPay = () => {
    if (payReportingTableData.payAmountReported !== null) {
      setPay(payReportingTableData.payAmountReported);
      payInput.setValue(payReportingTableData.payAmountReported.toString());
      setValidateSelfOnRender(true);
    }
  };

  const attributablePayModalProps: ComponentProps<typeof AttributablePayModal> = {
    hasAttributablePay,
    payMonth,
    attributablePay: statePayData?.attributablePay,
    firstName: payReportingTableData.employee.firstName,
    lastName: payReportingTableData.employee.lastName,
    employmentStartDate: payReportingTableData.employmentStartDate,
    payAmountString: payInput.props.value,
    onSave: onSaveAttributablePay,
    attributablePayReported: payReportingTableData.attributablePayReported,
    availableAttributablePayYears: payReportingTableData.availableAttributablePayYears,
  };

  return (
    <Table.Row customRowColor={payReportingTableData.isInactive ? 'black10' : undefined}>
      {showIconColumn && (
        <EmploymentTableRowIconCellOld employment={payReportingTableData} payMonthMargin={payMonthMargin} />
      )}
      <Table.Cell>
        <Text whiteSpace="nowrap" size="small">
          {payReportingTableData.employee.employeeId ? formatEmployeeId(payReportingTableData.employee.employeeId) : ''}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="small">{payReportingTableData.employee.firstName}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="small">{payReportingTableData.employee.lastName}</Text>
      </Table.Cell>
      <Table.Cell>
        {payReportingTableData.activeMarkup && (
          <Tooltip
            id={`tooltip-markup-${statePayData?.policyNumber}`}
            size="small"
            openOnHover={true}
            content={payReportingTableData.activeMarkup?.fokText || t('tooltips.markupMissingFallback.description')}
          >
            <Text size="small" color="black80">
              {payReportingTableData.activeMarkup?.fokId}
            </Text>
          </Tooltip>
        )}
      </Table.Cell>
      <Table.Cell textAlign="right">
        <Flex alignItems="center" position="relative">
          {payrollError || (payrollWarning && showTableFeedbackWarning) ? (
            <TableFeedbackOld
              type={payrollError ? 'error' : 'warning'}
              setShowWarningFeedback={setShowTableFeedBackWarning}
              onResetButtonClick={isDecreasedPayError ? resetToReportedPay : undefined}
              modalComponent={
                isAttributablePayError &&
                hasAttributablePay && (
                  <AttributablePayModal {...attributablePayModalProps} changeButtonFontSize="small" />
                )
              }
            >
              {payrollError ? payrollError : payrollWarning}
            </TableFeedbackOld>
          ) : (
            <AttributablePayModal {...attributablePayModalProps} />
          )}
        </Flex>
      </Table.Cell>
      <Table.Cell>
        <Flex alignItems="center">
          <InputField
            {...payInput.props}
            errors={[]}
            id={`pay-input-field-${index}`}
            label={t('table.manualReporting.columnPayAmount')}
            hideLabel={true}
            onBlur={onBlur}
            onKeyUp={onKeyBoardEvent}
            textAlign="right"
          />
          <Text marginLeft="10px" color="black70">
            {t('common.sekShorthand')}
          </Text>
        </Flex>
      </Table.Cell>
    </Table.Row>
  );
};
